/**
 * ReplayControls — play/pause, timeline with event markers, speed, jumps, follow.
 * The playback clock lives in the provider; this is pure UI.
 */
import React, { useMemo } from "react";
import { Flag, Pause, Play, SkipBack, SkipForward, Crosshair, Radio } from "lucide-react";
import { SPEED_OPTIONS, useRaceSession } from "../RaceSessionProvider";
import { formatSessionClock } from "../playback";

function CircleButton({ icon: Icon, label, onClick, active = false, primary = false, disabled = false, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            aria-label={label}
            aria-pressed={active}
            disabled={disabled}
            data-testid={testId}
            className={`grid h-10 w-10 place-items-center rounded-full border transition-all active:scale-95 disabled:opacity-35 ${
                active ? "border-f1-red/45 bg-f1-red/18 text-white shadow-[0_0_22px_rgba(225,6,0,0.22)]" : primary ? "border-white/20 bg-white/[0.08] text-white hover:border-f1-red/40 hover:bg-f1-red/12" : "border-white/10 bg-white/[0.04] text-zinc-300 hover:border-white/20 hover:text-white"
            }`}
        >
            <Icon className="h-4 w-4" />
        </button>
    );
}

export default function ReplayControls({ className = "" }) {
    const session = useRaceSession();
    const { manifest, mode, playing, speed, cursor, events, selectedEntryId, follow } = session;
    const time = manifest?.time;
    const raceStart = time?.race_start_seconds ?? null;
    const origin = time?.origin_seconds ?? 0;
    const end = time?.end_seconds ?? 0;
    // Two durations, kept apart: the winner's race (lights out to the first car's own flag) and the whole replay.
    const winnerFinish = time?.winner_finish_seconds ?? time?.chequered_seconds ?? null;
    const lastFinish = time?.last_finish_seconds ?? null;
    const markers = useMemo(() => {
        if (!time) return [];
        return (events || [])
            .filter((event) => event.session_seconds != null && event.status !== "retracted" && ["position_change", "pit", "phase", "retirement", "penalty"].includes(event.category))
            .map((event) => ({ id: event.id, left: ((event.session_seconds - origin) / Math.max(1, end - origin)) * 100, category: event.category, type: event.type, title: event.title }));
    }, [events, time, origin, end]);

    if (mode === "live") {
        return (
            <div className={`glass rounded-[24px] border border-white/8 px-4 py-3 ${className}`} data-testid="replay-controls">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                        <span className="text-f1-red">Live</span>
                        <span className="text-zinc-700"> · delayed {session.live?.live?.delay_seconds ?? "—"} s for feed coherence</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={() => session.setFollow(!follow)} disabled={!selectedEntryId} aria-pressed={follow} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-300 transition-all hover:border-f1-red/35 disabled:opacity-35">
                            <Crosshair className="h-3.5 w-3.5" /> Follow
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!time) return null;
    const position = ((cursor - origin) / Math.max(1, end - origin)) * 100;
    return (
        <div className={`glass rounded-[24px] border border-white/8 px-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.26)] ${className}`} data-testid="replay-controls" onClick={(event) => event.stopPropagation()}>
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                    <button type="button" onClick={session.jumpToStart} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-300 transition-all hover:border-f1-red/35 hover:text-white" data-testid="replay-jump-start">
                        <Flag className="h-3.5 w-3.5" /> Start
                    </button>
                    <CircleButton icon={SkipBack} label="Back 10 seconds" onClick={() => session.stepSeconds(-10)} testId="replay-step-back" />
                    <CircleButton icon={playing ? Pause : Play} label={playing ? "Pause" : "Play"} onClick={session.togglePlay} active={playing} primary testId="replay-toggle" />
                    <CircleButton icon={SkipForward} label="Forward 10 seconds" onClick={() => session.stepSeconds(10)} testId="replay-step-forward" />
                    <button type="button" onClick={session.jumpToFinish} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-300 transition-all hover:border-f1-red/35 hover:text-white" data-testid="replay-jump-finish">
                        <Flag className="h-3.5 w-3.5" /> Finish
                    </button>
                    <button type="button" onClick={() => session.setFollow(!follow)} disabled={!selectedEntryId} aria-pressed={follow} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-300 transition-all hover:border-f1-red/35 disabled:opacity-35" data-testid="replay-follow">
                        <Crosshair className="h-3.5 w-3.5" /> Follow
                    </button>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500" data-testid="replay-stage">
                        <span className="text-zinc-200">{cursor < (raceStart ?? -Infinity) ? "Pre-race" : winnerFinish != null && cursor >= winnerFinish ? (lastFinish != null && cursor >= lastFinish ? "After the race" : "Finishing") : "Race"}</span>
                    </div>
                    <div className="relative min-w-0 flex-1 pt-3">
                        <div className="pointer-events-none absolute left-0 right-0 top-0 h-2">
                            {markers.map((marker) => (
                                <span key={marker.id} title={marker.title} className={`absolute top-0 h-2 w-[2px] rounded-full ${marker.category === "pit" ? "bg-amber-300/60" : marker.category === "phase" ? "bg-sky-300/70" : marker.type === "on_track_pass" ? "bg-f1-red" : "bg-zinc-500/60"}`} style={{ left: `${marker.left}%` }} />
                            ))}
                        </div>
                        <input
                            type="range"
                            min={origin}
                            max={end}
                            step={0.5}
                            value={Math.min(Math.max(cursor, origin), end)}
                            onChange={(event) => session.seekTo(Number(event.target.value))}
                            aria-label="Replay timeline"
                            data-testid="replay-timeline"
                            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-[#E10600]"
                        />
                        {raceStart != null ? <span className="pointer-events-none absolute top-3 h-1.5 w-[2px] bg-white/70" style={{ left: `${((raceStart - origin) / Math.max(1, end - origin)) * 100}%` }} title="Lights out" /> : null}
                        {winnerFinish != null ? <span className="pointer-events-none absolute top-3 h-1.5 w-[2px] bg-amber-300/90" style={{ left: `${((winnerFinish - origin) / Math.max(1, end - origin)) * 100}%` }} title="Winner takes the flag" data-testid="replay-flag-marker" /> : null}
                        <span className="sr-only">{Math.round(position)}%</span>
                    </div>
                    <div className="min-w-[150px] text-right font-mono text-[11px] tracking-[0.18em] text-zinc-300" data-testid="replay-clock" data-winner-finish={winnerFinish ?? undefined} data-replay-end={end} title={winnerFinish != null && raceStart != null ? `Winner's race ${formatSessionClock(winnerFinish, raceStart)} · replay runs to ${formatSessionClock(end, raceStart)} after the flag` : undefined}>
                        {formatSessionClock(cursor, raceStart)}
                        {winnerFinish != null && raceStart != null ? (
                            cursor < winnerFinish ? (
                                <span className="text-zinc-600"> / {formatSessionClock(winnerFinish, raceStart)}</span>
                            ) : (
                                <span className="text-zinc-600"> · flag +{formatSessionClock(cursor - winnerFinish)} · ends {formatSessionClock(end, raceStart)}</span>
                            )
                        ) : (
                            <span className="text-zinc-600"> / {formatSessionClock(end, raceStart)}</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Speed</div>
                    <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] p-1" role="group" aria-label="Replay speed">
                        {SPEED_OPTIONS.map((value) => (
                            <button key={value} type="button" aria-pressed={speed === value} onClick={() => session.setSpeed(value)} className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${speed === value ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
                                {value}x
                            </button>
                        ))}
                    </div>
                    <button type="button" onClick={session.returnToLive} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white" data-testid="return-to-live">
                        <Radio className="h-3.5 w-3.5" /> Live
                    </button>
                </div>
            </div>
        </div>
    );
}
